import React from 'react';
import { v2, Icon } from '@fieldnation/platform-components';
import styles from './TrackingField.scss';
import { getCarrierFromTrackingNumber, getCarrierLabel, getTrackingNumber } from '../utils';
import FormError from './FormError';
import { CARRIER_TYPE } from '../constants';

const { TextInput } = v2;

export interface CarrierInfo {
  name: string;
  tracking: string;
  tracking_link?: string;
  other_name?: string;
}

interface TrackingFieldProps {
  value: CarrierInfo;
  onChange: (value: CarrierInfo) => void;
  disabled: boolean;
  inputLabel?: string;
  errors: {
    name: boolean;
    tracking: boolean;
  };
  optional?: boolean;
}

const TrackingField: React.FC<TrackingFieldProps> = ({
  value,
  onChange,
  disabled = false,
  errors,
  inputLabel = 'Tracking number',
  optional = false,
}: TrackingFieldProps): JSX.Element => {
  const handleTrackingChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const trackingNumber = getTrackingNumber(e.target.value);
    const { carrier, trackingLink } = getCarrierFromTrackingNumber(trackingNumber);
    onChange({
      ...value,
      name: carrier,
      tracking: trackingNumber,
      tracking_link: trackingLink,
      ...(carrier === CARRIER_TYPE.OTHER ? {} : { other_name: '' }),
    });
  };

  const handleCarrierNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onChange({
      ...value,
      other_name: e.target.value,
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.trackingFields}>
        <div className={styles.trackingInput}>
          <TextInput
            label={inputLabel}
            value={value.tracking || ''}
            onChange={handleTrackingChange}
            disabled={disabled}
            optional={optional}
          />
          {errors?.tracking === true ? <FormError message="Tracking number is required" /> : null}
        </div>
        {value.tracking && !value.tracking_link && value.name === CARRIER_TYPE.OTHER && (
          <div className={styles.carrierInput}>
            <TextInput
              label="Carrier name"
              value={value.name === CARRIER_TYPE.OTHER ? value?.other_name || '' : value.name}
              onChange={handleCarrierNameChange}
              disabled={disabled}
            />
            {errors?.name === true ? <FormError message="Carrier name is required" /> : null}
          </div>
        )}
      </div>
      {value.tracking && value.name !== CARRIER_TYPE.OTHER && value?.tracking_link && (
        <div className={styles.carrierInfo}>
          Carrier:
          {' '}
          <span className={styles.carrierName}>{getCarrierLabel(value.name)}</span>
          {' '}
          |
          {' '}
          <a
            href={value.tracking_link}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.trackLink}
          >
            Track shipment
            <Icon name="openInNew" size={16} />
          </a>
        </div>
      )}
    </div>
  );
};

TrackingField.defaultProps = {
  inputLabel: 'Tracking number',
  optional: false,
};

export default TrackingField;
